/**
 * MachinePage — redesign v2
 * RD-03: auto-loads first machine; live-loads on dropdown change (no "Get" button)
 * RD-08: Details tab removed; Control tab hidden (not broken)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import client from '../API/API';
import StatusMachines from '../containers/StatusMachines.jsx';
import KpiMachines from '../containers/KpiMachines.jsx';
import { MdFullscreen } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

const Icon = ({ d, size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const ChevronDown = 'M6 9l6 6 6-6';

// ── Machine selector dropdown ─────────────────────────────────────────────────
function MachineSelector({ machines, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = machines.find(m => m.machine_id === value);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 260 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: '100%', padding: '8px 12px',
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--r-md)', cursor: 'pointer',
          fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--ink-700)',
          transition: 'border-color var(--t-fast)',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
      >
        {selected && (
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--online)', flexShrink: 0, animation: 'atoms-pulse 2s infinite' }} />
        )}
        <span style={{ flex: 1, textAlign: 'left' }}>
          {selected ? (selected.machine_name || selected.machine_id) : 'Select machine'}
        </span>
        <Icon d={ChevronDown} size={14} color="var(--ink-300)" />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-lg)', zIndex: 200, maxHeight: 280, overflowY: 'auto' }}>
          {machines.map(m => (
            <button
              key={m.machine_id}
              onClick={() => { onChange(m.machine_id); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px', background: m.machine_id === value ? 'var(--accent-soft)' : 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 13, color: m.machine_id === value ? 'var(--accent)' : 'var(--ink-700)', textAlign: 'left' }}
              onMouseEnter={e => { if (m.machine_id !== value) e.currentTarget.style.background = 'var(--bg-raised)'; }}
              onMouseLeave={e => { if (m.machine_id !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 500 }}>{m.machine_name || m.machine_id}</span>
              {m.machine_name && m.machine_name !== m.machine_id && (
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-400)' }}>{m.machine_id}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const MachinePage = () => {
  const navigate = useNavigate();
  const [tabs,      setTabs]      = useState([]);
  const [machines,  setMachines]  = useState([]);
  const [machineId, setMachineId] = useState(null);
  const [activeTab, setActiveTab] = useState('iostatus');
  const [loading,   setLoading]   = useState(true);
  const [fullScreen, setFullscreen] = useState(false);
  const divRef = useRef(null);

  const activeMachine = useMemo(
    () => machines.find(m => m.machine_id === machineId) || null,
    [machines, machineId]
  );

  // RD-03: auto-load first machine
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [moduleRes, machinesRes] = await Promise.all([
          client.get('/api/machine_module/'),
          client.get('/api/machines/'),
        ]);
        const mList = machinesRes.data.machines || [];
        setTabs(moduleRes.data.sub_pages || []);
        setMachines(mList);
        if (mList.length > 0) setMachineId(mList[0].machine_id);
      } catch (err) {
        if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      } finally { setLoading(false); }
    };
    load();
  }, [navigate]);

  // RD-08: only show kpi (6) and status (7) — no details (5), no control (8)
  const VISIBLE_TABS = [
    { id: 6, value: 'kpi',      label: 'KPI'    },
    { id: 7, value: 'iostatus', label: 'Status' },
  ];
  const enabledTabs = VISIBLE_TABS.filter(t => tabs.includes(t.id));

  const handleFullScreen = () => {
    if (!document.fullscreenElement) {
      divRef.current?.requestFullscreen().then(() => setFullscreen(true));
    } else {
      document.exitFullscreen().then(() => setFullscreen(false));
    }
  };

  if (loading) return (
    <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--ink-300)', fontFamily: 'var(--font-sans)', fontSize: 13 }}>Loading machines…</div>
  );

  if (machines.length === 0) return (
    <div style={{ padding: '48px 0', textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink-700)' }}>No machines assigned</div>
      <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)', marginTop: 4 }}>Contact your administrator.</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* ── Machine header bar ── */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-hair)', borderRadius: 'var(--r-lg)', padding: '14px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', boxShadow: 'var(--sh-sm)' }}>
        <MachineSelector machines={machines} value={machineId} onChange={setMachineId} />
        {activeMachine && (
          <>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-500)' }}>
              {activeMachine.org_name || ''}{activeMachine.timezone ? ` · ${activeMachine.timezone}` : ''}
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-sans)', fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 4, background: 'var(--online-bg)', color: 'var(--online)', border: '1px solid var(--online-border)' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--online)', animation: 'atoms-pulse 2s infinite' }} />
              ONLINE
            </span>
          </>
        )}
      </div>

      {/* ── Tab bar + content ── */}
      {activeMachine && (
        <div ref={divRef} style={{ background: 'var(--bg-app)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border-hair)', marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 0 }}>
              {enabledTabs.map(t => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.value)}
                  style={{
                    padding: '12px 20px', background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--font-sans)', fontSize: 13,
                    fontWeight: activeTab === t.value ? 500 : 400,
                    color: activeTab === t.value ? 'var(--accent)' : 'var(--ink-500)',
                    borderBottom: `2px solid ${activeTab === t.value ? 'var(--accent)' : 'transparent'}`,
                    marginBottom: -1, transition: 'all var(--t-fast)',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={handleFullScreen} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-500)', padding: 8, marginRight: 4, borderRadius: 'var(--r-md)' }}>
              <MdFullscreen size={20} />
            </button>
          </div>

          {activeTab === 'kpi' && (
            <KpiMachines nodeid={activeMachine.id} machineid={activeMachine.machine_id} screensize={fullScreen} />
          )}
          {activeTab === 'iostatus' && (
            <StatusMachines nodeid={activeMachine.id} machineid={activeMachine.machine_id} statusChannels={activeMachine.status_channels || []} />
          )}
        </div>
      )}
    </div>
  );
};

export default MachinePage;
